// Fee & Obligation — Fees ledger HTTP transport: per-loan fee list +
// create/edit, the cross-loan Fees Ledger view, and the B3 fee review queue.
// Manual entries ride the review pipeline (pending_review → B3 approve
// creates the WF1 obligation → bills on the next statement → collects
// through the waterfall). Hooks in @/hooks/portfolioAdmin/useLoanFees wrap
// these with TanStack Query.

import { api } from "@/api/client";

// --- Vocabulary --------------------------------------------------------------

export type LoanFeeStatus =
  | "pending"
  | "pending_review"
  | "approved"
  | "billed"
  | "collected"
  | "waived"
  | "canceled";

export const LOAN_FEE_STATUSES: ReadonlyArray<{ value: LoanFeeStatus; label: string }> = [
  { value: "pending", label: "Pending" },
  { value: "pending_review", label: "In review" },
  { value: "approved", label: "Approved" },
  { value: "billed", label: "Billed" },
  { value: "collected", label: "Collected" },
  { value: "waived", label: "Waived" },
  { value: "canceled", label: "Canceled" },
];

// Fee Review — fee rows awaiting a B3 approve/waive decision. sourceType
// marks who created the row (an automatic assessment engine, or staff via
// the Add Fee modal); triggerDescription is the plain-words explanation.

export type FeeReviewSourceType =
  | "late_fee"
  | "nsf_fee"
  | "default_interest"
  | "staff_manual"
  | "inspection_audit_cost"
  | "collection_enforcement_cost"
  | "lender_cure_cost";

export type FeeReviewStatus =
  | "pending_review"
  | "approved"
  | "waived"
  | "billed"
  | "collected";

export const FEE_REVIEW_SOURCE_TYPES: ReadonlyArray<{
  value: FeeReviewSourceType;
  label: string;
}> = [
  { value: "late_fee", label: "Late fee" },
  { value: "nsf_fee", label: "NSF fee" },
  { value: "default_interest", label: "Default interest" },
  { value: "staff_manual", label: "Staff-entered" },
  { value: "inspection_audit_cost", label: "Recovery cost — inspection/audit" },
  { value: "collection_enforcement_cost", label: "Recovery cost — collection/enforcement" },
  { value: "lender_cure_cost", label: "Recovery cost — lender cure" },
];

// Payment-channel ordinals — the canonical channel numbers (shared with
// IncomeEvent.PaymentChannel). The ordinal IS the stored value.
export const PAYMENT_CHANNELS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: "Phoenix direct — from borrower" },
  { value: 2, label: "Phoenix reimbursed — from seller income" },
  { value: 3, label: "Processor — from borrower" },
  { value: 4, label: "Processor — from seller income" },
  { value: 5, label: "Phoenix — from investor / pool" },
  { value: 6, label: "Phoenix direct — from vendor" },
];

// --- Shapes ------------------------------------------------------------------

export interface LoanFeeDto {
  id: string;
  loanId: string;
  loanNumber: string | null;
  feeType: string;
  description: string | null;
  amount: number;
  revenuePoolId: string | null;
  revenuePoolCode: string | null;
  paymentChannel: number;
  payer: string;
  status: LoanFeeStatus;
  incurredDate: string;
  billedDate: string | null;
  collectedDate: string | null;
  notes: string | null;
  /** Null = record-only tracker row (staff-managed status ladder); non-null =
   * review-pipeline row whose status is managed by B3 approve/waive +
   * statement billing — the inline status actions must not render for it. */
  sourceType?: string | null;
  /** Wizard metadata (PR-2). */
  targetObligationTypeCode?: string | null;
  lifecycleType?: string | null;
  lifecycleDetail?: string | null;
  customLabel?: string | null;
  /** Approve-time back-link to the Obligation the fee funded. */
  obligationId?: string | null;
}

// --- Add Obligation wizard catalog (PR-2) --------------------------------------

/** One manually-addable obligation type with its live WF1 slot facts and the
 * wizard's smart-default template. isCurrentlyFireable is DERIVED server-side
 * from the slot's fires-on codes vs the emitted set — dormant P4-P6 rows
 * render the "recorded and billed now; collection begins at wake-up" line. */
export interface AddableObligationTypeDto {
  typeCode: string;
  displayName: string;
  description: string | null;
  priorityPosition: number | null;
  isCurrentlyFireable: boolean;
  isTakeAllRemaining: boolean;
  recipientResolution: string | null;
  recipientDescription: string | null;
  firesOn: string[];
  defaultPaymentChannel: number;
  defaultPoolCode: string | null;
  defaultLifecycle: string;
  confirmBeforeAdding: boolean;
}

export interface WaterfallLadderRowDto {
  position: number;
  typeCode: string;
  label: string;
  isTakeAllRemaining: boolean;
}

export interface FeeObligationCatalogDto {
  types: AddableObligationTypeDto[];
  ladder: WaterfallLadderRowDto[];
  customFallbackTypeCode: string;
}

// --- Ask AI (obligation-creation coach, PR-2) ----------------------------------

export interface ObligationExplainTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ObligationCreationExplainContext {
  typeCode?: string | null;
  customLabel?: string | null;
  amount?: number | null;
  incurredDate?: string | null;
  lifecycleType?: string | null;
  lifecycleDetail?: string | null;
  poolCode?: string | null;
  payerName?: string | null;
  beneficiaryName?: string | null;
  stepKey?: string | null;
}

export interface ObligationCreationExplainRequest {
  question: string | null;
  history: ObligationExplainTurn[] | null;
  context?: ObligationCreationExplainContext;
}

export interface ObligationCreationExplainDto {
  answer: string;
}

export interface LoanFeesSummaryDto {
  count: number;
  totalAmount: number;
  pendingAmount: number;
  billedAmount: number;
  collectedAmount: number;
}

export interface LoanFeesDto {
  loanId: string;
  loanNumber: string;
  fees: LoanFeeDto[];
  summary: LoanFeesSummaryDto;
}

export interface CrossLoanFeesDto {
  fees: LoanFeeDto[];
  summary: LoanFeesSummaryDto;
}

export interface CreateLoanFeeRequest {
  feeType: string;
  description?: string | null;
  amount: number;
  revenuePoolId?: string | null;
  paymentChannel: number;
  payer: string;
  incurredDate: string;
  notes?: string | null;
  /** Wizard-chosen WF1 obligation type (server-validated allow-list). */
  targetObligationTypeCode?: string | null;
  /** one_time | event_triggered | recurring (metadata). */
  lifecycleType?: string | null;
  lifecycleDetail?: string | null;
  /** Custom/other entry name — routes to the catalog's fallback slot and
   * flags the row for admin catalog review. */
  customLabel?: string | null;
  /** Party provenance metadata (per-type entity ids, display only). */
  payerEntityId?: string | null;
  beneficiaryEntityId?: string | null;
}

export interface UpdateLoanFeeRequest {
  feeType?: string | null;
  description?: string | null;
  amount?: number | null;
  revenuePoolId?: string | null;
  // A bare null revenuePoolId means "unchanged"; set clearRevenuePool to
  // remove an existing pool tag on edit.
  clearRevenuePool?: boolean;
  paymentChannel?: number | null;
  payer?: string | null;
  status?: LoanFeeStatus | null;
  notes?: string | null;
}

export interface CrossLoanFeesParams {
  status?: string;
  payer?: string;
}

export interface FeeReviewItemDto {
  id: string;
  loanId: string;
  loanNumber: string | null;
  feeType: string;
  sourceType: FeeReviewSourceType;
  amount: number;
  periodStart: string | null;
  incurredDate: string;
  triggerDescription: string | null;
  status: FeeReviewStatus;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  waiveReason: string | null;
}

export interface FeeReviewQueueDto {
  items: FeeReviewItemDto[];
  /** Book-wide pending_review count — independent of the status filter. */
  pendingCount: number;
}

export interface FeeReviewQueueParams {
  status?: string;
  loanId?: string;
}

// --- Transport ---------------------------------------------------------------

export const loanFeesApi = {
  forLoan: (loanId: string) =>
    api.get<LoanFeesDto>(`/portfolio-admin/loans/${loanId}/fees`),
  create: (loanId: string, body: CreateLoanFeeRequest) =>
    api.post<LoanFeeDto>(`/portfolio-admin/loans/${loanId}/fees`, body),
  update: (loanId: string, feeId: string, body: UpdateLoanFeeRequest) =>
    api.patch<LoanFeeDto>(`/portfolio-admin/loans/${loanId}/fees/${feeId}`, body),
  crossLoan: (params?: CrossLoanFeesParams) =>
    api.get<CrossLoanFeesDto>(`/portfolio-admin/fees`, params),
  // Fee Review — B3 approve/waive of system-assessed fees (Fees.Review).
  reviewQueue: (params?: FeeReviewQueueParams) =>
    api.get<FeeReviewQueueDto>(`/portfolio-admin/loan-fees/review-queue`, params),
  approve: (feeId: string) =>
    api.post<FeeReviewItemDto>(`/portfolio-admin/loan-fees/${feeId}/approve`),
  waive: (feeId: string, reason: string) =>
    api.post<FeeReviewItemDto>(`/portfolio-admin/loan-fees/${feeId}/waive`, { reason }),
  // Add Obligation wizard (PR-2).
  obligationCatalog: (loanId: string) =>
    api.get<FeeObligationCatalogDto>(`/portfolio-admin/loans/${loanId}/obligation-catalog`),
  explainObligationCreation: (loanId: string, body: ObligationCreationExplainRequest) =>
    api.post<ObligationCreationExplainDto>(
      `/portfolio-admin/loans/${loanId}/fee-obligation/explain`, body),
};
